import { useMemo, useState } from 'react';

import { useLanguage } from '../../contexts/language';
import { usePatients } from '../../hooks/patients';

import { ActionLink } from '../action-link';
import { FilterChips } from '../filter-chips';
import { InvitedRow } from '../invited-row';
import { LoadFailed } from '../load-failed';
import { PatientRow } from '../patient-row';
import { SearchField } from '../search-field';

import styles from './style.module.css';

// Whether a person matches what was typed in the search box: every word
// typed appears somewhere in what is shown for them, whatever the case.
function matches({ shown, search }) {
  const haystack = shown.join(' ').toLowerCase();

  return search.toLowerCase().split(/\s+/).every(function(word) {
    return haystack.includes(word);
  });
}

// The clinician's Patients section, as drawn in the blueprint: Invite
// patient beside the title, a search by name, and two filters, the patients
// they care for (Active) and the invitations not answered yet (Invited).
export function Patients() {
  const { text } = useLanguage();
  const { data, error, retry } = usePatients();
  const [ search, setSearch ] = useState('');
  const [ filter, setFilter ] = useState('active');

  const rows = useMemo(function() {
    if (data === undefined) {
      return [];
    }

    if (filter === 'invited') {
      return data.sentInvitations.filter(function({ status, name, email }) {
        const shown = [ email ];

        if (name != null) {
          shown.push(name);
        }

        return status === 'PENDING' && matches({ shown, search });
      }).map(function(invitation) {
        return <InvitedRow key={ invitation.id } invitation={ invitation }/>;
      });
    }

    return data.patients.filter(function({ firstName, lastName }) {
      return matches({ shown: [ firstName, lastName ], search });
    }).map(function(patient) {
      return <PatientRow key={ patient.id } patient={ patient }/>;
    });
  }, [ data, filter, search ]);

  if (error != null) {
    return <LoadFailed onRetry={ retry }/>;
  }

  if (data === undefined) {
    return null;
  }

  const invited = data.sentInvitations.filter(function({ status }) {
    return status === 'PENDING';
  });

  const options = [
    { value: 'active', label: text.active, count: data.patients.length },
    { value: 'invited', label: text.invited, count: invited.length }
  ];

  let list = <p className={ styles.empty }>{ text.noPatientsFound }</p>;

  if (rows.length > 0) {
    list = <section className={ styles.list } aria-label={ text.patients }>{ rows }</section>;
  }

  return (
    <section className={ styles.patients }>
      <div className={ styles.header }>
        <h1 className={ styles.title }>{ text.patients }</h1>

        <ActionLink to='/patients/invite'>{ text.invitePatient }</ActionLink>
      </div>

      <div className={ styles.tools }>
        <SearchField id='patients-search' label={ text.searchPatients } placeholder={ text.searchByName } value={ search } onChange={ setSearch }/>

        <FilterChips label={ text.filter } options={ options } value={ filter } onChange={ setFilter }/>
      </div>

      { list }
    </section>
  );
}
